import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  CreditCard, CheckCircle2, Zap, RefreshCw, Clock, Receipt, Loader2,
  Check, Building2, Crown, BedDouble, Sparkles, FileText, Download, Wallet
} from 'lucide-react';
import axios from 'axios';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const getAuthHeaders = () => {
  const token = localStorage.getItem('einvoice_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PLAN_ICONS = {
  starter: BedDouble,
  growth: Zap,
  pro: Building2,
  enterprise: Crown,
};

const PLAN_GRADIENT = {
  starter: 'from-slate-500 to-slate-600',
  growth: 'from-blue-500 to-blue-600',
  pro: 'from-purple-500 to-purple-600',
  enterprise: 'from-amber-500 to-amber-600',
};

/* ─── Subscription Tab ─── */
function SubscriptionTab({ plans, currentSubscription, processingPlan, onSubscribe, onSubscribeCustom, formatPrice }) {
  const isOnCustom = !!currentSubscription?.admin_plan?.is_custom;
  const isSubAdmin = !!currentSubscription?.admin_plan?.is_sub_admin;
  const stripeSub = currentSubscription?.subscription || null;
  const isCustomActive = isOnCustom && stripeSub && stripeSub.status === 'active';
  const nextBillingIso = stripeSub?.next_billing_date || stripeSub?.expires_at || stripeSub?.current_period_end;
  const nextBillingDate = nextBillingIso ? new Date(nextBillingIso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  return (
    <div className="space-y-6" data-testid="subscription-tab">
      {/* Current Plan Banner */}
      {currentSubscription?.has_subscription && (
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400 font-medium">Current Plan</p>
                  <p className="text-xl font-bold text-green-800 dark:text-green-200">
                    {currentSubscription.subscription?.plan_name}
                  </p>
                  <Badge variant="outline" className="mt-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
                    {currentSubscription.is_active ? 'Active' : 'Expired'}
                  </Badge>
                </div>
              </div>
              {currentSubscription.subscription?.next_billing_date && (
                <div className="text-right">
                  <p className="text-sm text-green-600 dark:text-green-400">Next Billing</p>
                  <p className="font-medium text-green-800 dark:text-green-200">
                    {new Date(currentSubscription.subscription.next_billing_date).toLocaleDateString('en-MY', {
                      day: 'numeric', month: 'short', year: 'numeric'
                    })}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">Billed monthly on the 1st</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Plan action card (only visible when user is on Custom AND is billable) */}
      {isOnCustom && !isSubAdmin && (
        <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/30 dark:to-slate-900" data-testid="custom-plan-checkout-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Custom Plan</h3>
                  <Badge className="bg-amber-500 text-white">Assigned by StayMii</Badge>
                </div>
                <p className="text-sm text-slate-600 dark:text-gray-300 mb-3">Your organization has been assigned a bespoke monthly subscription price agreed with StayMii. All Pro features are included.</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Unlimited invoices &amp; API access</li>
                  <li>• Everything in Pro, plus dedicated support</li>
                  <li>• Bespoke pricing agreed with StayMii</li>
                </ul>
              </div>
              <div className="md:text-right md:min-w-[220px]">
                <p className="text-4xl font-bold text-amber-700">
                  RM {Number(currentSubscription.admin_plan.price || 0).toFixed(2)}
                </p>
                <p className="text-xs text-slate-500 mb-3">per month</p>
                {isCustomActive ? (
                  <div className="space-y-2">
                    <Badge className="bg-green-600 text-white w-full justify-center py-1.5" data-testid="custom-plan-active-badge">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Active
                    </Badge>
                    {nextBillingDate && (
                      <p className="text-[11px] text-slate-500">Next billing on {nextBillingDate}</p>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={() => onSubscribeCustom && onSubscribeCustom()}
                    disabled={processingPlan === 'custom'}
                    className="w-full md:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                    data-testid="custom-plan-subscribe-btn"
                  >
                    {processingPlan === 'custom' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                    ) : (
                      <><CreditCard className="h-4 w-4 mr-2" />Subscribe Now</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sub-admin banner */}
      {isSubAdmin && (
        <Card className="border-slate-200 bg-slate-50" data-testid="subadmin-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-slate-500 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-slate-800">Billed under primary account</p>
              <p className="text-xs text-slate-500 mt-0.5">
                This account inherits its plan from <span className="font-medium">{currentSubscription?.admin_plan?.billing_parent_email || 'the primary account'}</span>. No separate subscription or payment is required here.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Cards — hidden when user is on Custom or is a sub-admin */}
      {!isOnCustom && !isSubAdmin && (
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((plan) => {
          const Icon = PLAN_ICONS[plan.id] || Zap;
          const gradient = PLAN_GRADIENT[plan.id] || 'from-blue-500 to-blue-600';
          const isCurrentPlan = currentSubscription?.subscription?.plan_id === plan.id;
          const isPopular = plan.popular === true;
          const isEnterprise = plan.custom_pricing === true;

          return (
            <Card
              key={plan.id}
              className={`relative overflow-hidden transition-all hover:shadow-lg ${
                isPopular ? 'border-blue-400 dark:border-blue-500 ring-2 ring-blue-400 dark:ring-blue-600' : ''
              } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {isPopular && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-none rounded-bl-lg bg-blue-600">Most Popular</Badge>
                </div>
              )}
              {isCurrentPlan && (
                <div className="absolute top-0 left-0">
                  <Badge className="rounded-none rounded-br-lg bg-green-600">Current Plan</Badge>
                </div>
              )}

              <CardHeader className="pb-4">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center mb-4`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <CardTitle className="text-xl dark:text-white">{plan.name}</CardTitle>
                {plan.description && (
                  <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">{plan.description}</p>
                )}
                <CardDescription className="mt-2">
                  {isEnterprise ? (
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">Custom Pricing</span>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-slate-900 dark:text-white">
                        {formatPrice(plan.price, plan.currency)}
                      </span>
                      <span className="text-slate-500 dark:text-gray-400">/month</span>
                    </>
                  )}
                </CardDescription>
                {plan.room_limit > 0 && (
                  <Badge variant="outline" className="mt-2 bg-slate-50 dark:bg-zinc-800">
                    <BedDouble className="h-3 w-3 mr-1" />
                    Up to {plan.room_limit} rooms
                  </Badge>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {plan.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  className={`w-full ${isPopular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  variant={isCurrentPlan ? 'outline' : isEnterprise ? 'outline' : 'default'}
                  disabled={isCurrentPlan || processingPlan === plan.id}
                  onClick={() =>
                    isEnterprise
                      ? (window.location.href = 'mailto:sales@staymii.com?subject=Enterprise Plan Inquiry')
                      : onSubscribe(plan.id)
                  }
                  data-testid={`subscribe-btn-${plan.id}`}
                >
                  {processingPlan === plan.id ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : isEnterprise ? (
                    'Contact Sales'
                  ) : (
                    <><CreditCard className="h-4 w-4 mr-2" />Subscribe Now</>
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
      )}

      {/* Billing Note */}
      <div className="bg-slate-50 dark:bg-zinc-900 rounded-lg p-4 text-center">
        <p className="text-sm text-slate-600 dark:text-gray-400">
          <span className="font-medium">Billing aligned to the 1st:</span> Sign up anytime and pay only for the remaining days this month.
          Full monthly billing starts on the 1st of next month.
        </p>
      </div>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg dark:text-white">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { q: 'How is the room limit counted?', a: "Room limits are based on the total number of rooms across all your connected properties. If you have 2 properties with 15 rooms each, that's 30 rooms total (Growth plan)." },
            { q: 'What happens if I exceed my room limit?', a: "We use soft limits - your invoices will never be blocked. We'll notify you to upgrade when you're approaching the limit." },
            { q: 'Can I change my plan later?', a: 'Yes, you can upgrade anytime. Downgrades take effect on your next billing cycle. Pro-rated credits apply.' },
            { q: 'Is there a free trial?', a: 'Yes! 14-day free trial on all plans. No credit card required to start.' },
            { q: 'How does billing work?', a: 'All subscriptions are billed on the 1st of each month. When you first subscribe, you pay a prorated amount for the remaining days in the current month.' },
          ].map((faq, i) => (
            <div key={i}>
              <p className="font-medium text-slate-900 dark:text-white">{faq.q}</p>
              <p className="text-sm text-slate-600 dark:text-gray-400">{faq.a}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="text-center py-4 border-t dark:border-white/10">
        <p className="text-sm text-slate-500 dark:text-gray-400">
          14-day free trial &nbsp;-&nbsp; No credit card required &nbsp;-&nbsp; Cancel anytime &nbsp;-&nbsp; 30-day money-back guarantee
        </p>
      </div>
    </div>
  );
}

/* ─── Payments Tab ─── */
function PaymentsTab({ subscription }) {
  const hasCard = subscription?.payment_method;

  return (
    <div className="space-y-6" data-testid="payments-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 dark:text-white">
            <Wallet className="w-4 h-4 text-[#4169E1]" />
            Payment Method
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasCard ? (
            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg">
              <div className="w-12 h-8 bg-gradient-to-br from-slate-700 to-slate-900 rounded flex items-center justify-center">
                <CreditCard className="w-5 h-3.5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {subscription.payment_method.brand || 'Card'} ending in {subscription.payment_method.last4 || '****'}
                </p>
                <p className="text-xs text-slate-500 dark:text-gray-400">
                  Expires {subscription.payment_method.exp_month}/{subscription.payment_method.exp_year}
                </p>
              </div>
              <Badge variant="outline" className="text-green-600 border-green-300 dark:text-green-400 dark:border-green-700">Default</Badge>
            </div>
          ) : (
            <div className="text-center py-10">
              <CreditCard className="w-10 h-10 mx-auto text-slate-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-slate-500 dark:text-gray-400">No payment method on file</p>
              <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">A payment method will be added when you subscribe to a plan</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 dark:text-white">
            <Building2 className="w-4 h-4 text-[#4169E1]" />
            Billing Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Billing Email</p>
              <p className="text-sm font-medium text-slate-900 dark:text-white">
                {subscription?.billing_email || (() => { try { return JSON.parse(localStorage.getItem('einvoice_user') || '{}').email; } catch { return 'Not set'; } })()}
              </p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Billing Cycle</p>
              <p className="text-sm font-medium text-slate-900 dark:text-white">Monthly (1st of each month)</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 dark:text-gray-500 mt-4">
            Payment methods are managed securely through Stripe. To update your card, subscribe to a new plan or contact support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Invoices Tab ─── */
function InvoicesTab({ transactions }) {
  return (
    <div className="space-y-6" data-testid="invoices-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 dark:text-white">
            <Clock className="w-4 h-4 text-[#4169E1]" />
            Billing Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-zinc-800">
                    <th className="text-left py-2 px-3 text-xs text-slate-500 dark:text-gray-400 uppercase">Date</th>
                    <th className="text-left py-2 px-3 text-xs text-slate-500 dark:text-gray-400 uppercase">Description</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-500 dark:text-gray-400 uppercase">Amount</th>
                    <th className="text-center py-2 px-3 text-xs text-slate-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="text-center py-2 px-3 text-xs text-slate-500 dark:text-gray-400 uppercase">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn, i) => (
                    <tr key={txn.session_id || i} className="border-b border-slate-100 dark:border-zinc-800 last:border-0">
                      <td className="py-3 px-3 text-slate-600 dark:text-gray-300">
                        {new Date(txn.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3 px-3 text-slate-900 dark:text-white font-medium capitalize">
                        {txn.plan_id === 'custom' ? 'Custom Plan' : `${txn.plan_id} Plan`}
                        {txn.receipt_number && (
                          <div className="text-[10px] text-slate-400 font-mono normal-case mt-0.5">{txn.receipt_number}</div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right font-medium text-slate-900 dark:text-white">
                        RM {txn.amount?.toFixed(2)}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          txn.payment_status === 'paid'
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                            : txn.payment_status === 'pending'
                            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                            : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-gray-300'
                        }`}>
                          {txn.payment_status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {(txn.invoice_url || txn.receipt_url) ? (
                          <a href={txn.invoice_url || txn.receipt_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#4169E1] hover:underline"
                            data-testid={`download-invoice-${i}`}
                          >
                            <Download className="w-3 h-3" /> Receipt
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-slate-500 dark:text-gray-400">
              <Receipt className="w-10 h-10 mx-auto text-slate-300 dark:text-gray-600 mb-2" />
              <p className="text-sm">No billing invoices yet</p>
              <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Invoices will appear here once you subscribe to a plan</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Main Billing Page ─── */
export default function PortalBilling() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('subscription');
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [billingPlans, setBillingPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(false);

  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/auth/login'); return; }
    loadAllData();
  }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPollingStatus(true);
      pollPaymentStatus(sessionId, 0);
    }
  }, [searchParams]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();

      // Fetch from both API sets in parallel
      const [paymentPlansRes, billingPlansRes, subRes, paymentSubRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/api/einvoice/payments/plans`).catch(() => ({ data: { plans: [] } })),
        axios.get(`${API_URL}/api/einvoice/billing/plans`).catch(() => ({ data: { plans: [] } })),
        axios.get(`${API_URL}/api/einvoice/payments/subscription`, { headers: authHeaders }).catch(() => ({ data: { has_subscription: false } })),
        axios.get(`${API_URL}/api/einvoice/billing/subscription`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/einvoice/billing/history`, { headers }).catch(() => ({ data: { transactions: [] } })),
      ]);

      // Use whichever plan source has data
      const payPlans = paymentPlansRes.data.plans || [];
      const billPlans = billingPlansRes.data.plans || [];
      setPlans(payPlans.length > 0 ? payPlans : []);
      setBillingPlans(billPlans);
      setCurrentSubscription(subRes.data);
      setSubscription(paymentSubRes.data);
      setTransactions(histRes.data.transactions || []);
    } catch (err) {
      console.error('Load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const originUrl = window.location.origin;
      const einvoiceToken = localStorage.getItem('einvoice_token');
      if (!einvoiceToken) {
        toast.error('Please log in to subscribe.');
        setProcessingPlan(null);
        return;
      }

      // Use fetch() to bypass global axios interceptor and ensure einvoice token is used
      let checkoutUrl = null;
      let lastError = '';

      // Try payments API first
      try {
        const res = await fetch(`${API_URL}/api/einvoice/payments/subscribe`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${einvoiceToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_id: planId, origin_url: originUrl }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          lastError = err.detail || `Server error (${res.status})`;
          throw new Error(lastError);
        }
        const data = await res.json();
        checkoutUrl = data.checkout_url;
      } catch (e) {
        lastError = e.message || 'Payment API unavailable';
        // Fallback to billing API
        try {
          const res = await fetch(`${API_URL}/api/einvoice/billing/checkout`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${einvoiceToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan_id: planId, origin_url: originUrl }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            lastError = err.detail || `Server error (${res.status})`;
            throw new Error(lastError);
          }
          const data = await res.json();
          checkoutUrl = data.url;
        } catch (e2) {
          lastError = e2.message || lastError;
        }
      }

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.error(`Failed to start checkout: ${lastError}`);
        setProcessingPlan(null);
      }
    } catch (error) {
      toast.error(`Failed to start checkout: ${error.message || 'Please try again.'}`);
      setProcessingPlan(null);
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempts) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setPollingStatus(false);
      toast.error('Payment verification timed out. Please refresh the page.');
      setSearchParams({});
      return;
    }
    try {
      const einvoiceToken = localStorage.getItem('einvoice_token');
      const authHdr = einvoiceToken ? { 'Authorization': `Bearer ${einvoiceToken}` } : {};

      let data = null;
      try {
        const res = await fetch(`${API_URL}/api/einvoice/payments/status/${sessionId}`, { headers: authHdr });
        if (res.ok) data = await res.json();
      } catch {}
      if (!data) {
        try {
          const res = await fetch(`${API_URL}/api/einvoice/billing/checkout/status/${sessionId}`, { headers: authHdr });
          if (res.ok) data = await res.json();
        } catch {}
      }

      if (data?.payment_status === 'paid') {
        setPollingStatus(false);
        toast.success('Payment successful! Your subscription is now active.');
        setSearchParams({});
        await loadAllData();
        return;
      }
      if (data?.status === 'expired') {
        setPollingStatus(false);
        toast.error('Payment session expired. Please try again.');
        setSearchParams({});
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
    }
  }, []);

  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: (currency || 'MYR').toUpperCase(),
    }).format(price);
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="w-6 h-6 animate-spin text-[#4169E1]" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="portal-billing">
        {/* Payment Polling Overlay */}
        {pollingStatus && (
          <div className="fixed inset-0 bg-slate-50/80 dark:bg-black/60 z-50 flex items-center justify-center">
            <Card className="w-80 text-center p-6" data-testid="payment-polling">
              <Loader2 className="w-10 h-10 animate-spin text-[#4169E1] mx-auto mb-3" />
              <p className="font-semibold text-slate-900 dark:text-white">Verifying payment...</p>
              <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">Please wait while we confirm your payment</p>
            </Card>
          </div>
        )}

        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#4169E1]" data-testid="billing-title">Billing</h1>
          <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">Manage your subscription, payments, and invoices</p>
        </div>

        {/* Current Plan Summary */}
        {(currentSubscription?.has_subscription || subscription?.status === 'active') && (
          <Card data-testid="current-plan-summary">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-gray-400">
                      {currentSubscription?.admin_plan?.is_sub_admin ? 'Inherited Plan' : 'Active Plan'}
                    </p>
                    <p className="text-lg font-bold text-slate-900 dark:text-white" data-testid="current-plan-name">
                      {currentSubscription?.admin_plan?.plan_name || currentSubscription?.subscription?.plan_name || subscription?.plan_name || 'Active'}
                      {currentSubscription?.admin_plan?.is_custom && (
                        <span className="ml-2 text-sm font-semibold text-emerald-700">RM {Number(currentSubscription.admin_plan.price).toFixed(2)}/month</span>
                      )}
                    </p>
                    {currentSubscription?.admin_plan?.is_sub_admin && currentSubscription.admin_plan.billing_parent_email && (
                      <p className="text-xs text-slate-500 mt-0.5">Billed under primary account: <span className="font-medium">{currentSubscription.admin_plan.billing_parent_email}</span> — no separate charges for this account.</p>
                    )}
                  </div>
                </div>
                <Badge className={`self-start sm:self-center ${currentSubscription?.admin_plan?.is_custom ? 'bg-amber-500 text-white' : 'bg-green-600 text-white'}`} data-testid="plan-status-badge">
                  {currentSubscription?.admin_plan?.is_custom ? 'Custom' : 'Active'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom Plan detail card (only if Super Admin assigned Custom to this user) */}
        {currentSubscription?.admin_plan?.is_custom && !currentSubscription?.admin_plan?.is_sub_admin && (
          <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30" data-testid="custom-plan-detail">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Your Custom Plan</h3>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-gray-300 mb-2">A bespoke monthly subscription price agreed with StayMii.</p>
                  {currentSubscription.admin_plan.notes && (
                    <p className="text-xs text-slate-500 italic">Note: {currentSubscription.admin_plan.notes}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-amber-700">RM {Number(currentSubscription.admin_plan.price).toFixed(2)}</p>
                  <p className="text-xs text-slate-500">per month</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full sm:w-auto bg-slate-100 dark:bg-zinc-800 p-1 rounded-lg" data-testid="billing-tabs">
            <TabsTrigger
              value="subscription"
              className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4"
              data-testid="tab-subscription"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Subscription
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4"
              data-testid="tab-payments"
            >
              <CreditCard className="w-3.5 h-3.5" />
              Payments
            </TabsTrigger>
            <TabsTrigger
              value="invoices"
              className="flex items-center gap-1.5 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-[#4169E1] px-4"
              data-testid="tab-invoices"
            >
              <FileText className="w-3.5 h-3.5" />
              Invoices
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscription" className="mt-6">
            <SubscriptionTab
              plans={plans}
              currentSubscription={currentSubscription}
              processingPlan={processingPlan}
              onSubscribe={handleSubscribe}
              onSubscribeCustom={() => handleSubscribe('custom')}
              formatPrice={formatPrice}
            />
          </TabsContent>

          <TabsContent value="payments" className="mt-6">
            <PaymentsTab subscription={subscription} />
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <InvoicesTab transactions={transactions} />
          </TabsContent>
        </Tabs>
      </div>
    </PortalLayout>
  );
}
